// NodeContextMenu.js - Context menu for node operations

import React, { useEffect } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';
import useStore from '../../store/useStore';
import { getAvailableDataSources } from '../../utils/dataSourceHelpers';

const NodeContextMenu = ({ position, node, onClose, onDelete, onClone, onAlias, onChangeDataSources, onUploadS2P, onExportSweepCSV }) => {
  const { componentLibrary, frequencyMode, sweepResults, showTutorial, tutorialStep, completeTutorialAction, openDatasheetModal } = useStore();

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleAlias = () => {
    onAlias();
    onClose();
  };

  const handleClone = () => {
    onClone();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  const handleViewDatasheet = () => {
    // Open datasheet in modal (uses server proxy with caching)
    const partNumber = node.data.partNumber;
    openDatasheetModal(partNumber);
    // Track for tutorial (step 16 = view-datasheet)
    if (showTutorial && tutorialStep === 16) {
      completeTutorialAction('datasheetViewed');
    }
    onClose();
  };

  // Check if this is a Guerrilla RF catalog component
  const isGuerrillaRFCatalog = node.data.isCatalog &&
                               node.data.manufacturer === 'Guerrilla RF' &&
                               node.data.partNumber;

  // Check if component has multiple data sources available
  const dataSourceParams = ['gain', 'noiseFigure', 'p1db', 'oip3'];
  const hasMultipleDataSources = node.data.isCatalog && dataSourceParams.some(paramKey => {
    const availableSources = getAvailableDataSources(node, paramKey, componentLibrary);
    return availableSources.length > 1;
  });

  const handleChangeDataSources = () => {
    onChangeDataSources();
    onClose();
  };

  const handleUploadS2P = () => {
    onUploadS2P();
    onClose();
  };

  const handleExportSweepCSV = () => {
    onExportSweepCSV();
    onClose();
  };

  // Check if this is a catalog component (can upload S2P)
  const isCatalogComponent = node.data.isCatalog;

  // Check if this is a measurement node in sweep mode with data
  const isMeasurementInSweepMode = node.type === 'measurement' &&
    frequencyMode === 'sweep' &&
    sweepResults &&
    sweepResults.frequencies &&
    sweepResults.frequencies.length > 0;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Menu style={{ top: position.y, left: position.x }}>
        {isGuerrillaRFCatalog && (
          <MenuItem onClick={handleViewDatasheet}>
            <Icon>📄</Icon>
            <MenuText>View Data Sheet</MenuText>
          </MenuItem>
        )}
        {hasMultipleDataSources && (
          <MenuItem onClick={handleChangeDataSources}>
            <Icon>📊</Icon>
            <MenuText>Change Data Sources</MenuText>
          </MenuItem>
        )}
        {isCatalogComponent && (
          <MenuItem onClick={handleUploadS2P}>
            <Icon>📈</Icon>
            <MenuText>Upload S2P File</MenuText>
          </MenuItem>
        )}
        {isMeasurementInSweepMode && (
          <MenuItem onClick={handleExportSweepCSV}>
            <Icon>📥</Icon>
            <MenuText>Export Sweep to CSV</MenuText>
          </MenuItem>
        )}
        <MenuItem onClick={handleAlias}>
          <Icon>✏️</Icon>
          <MenuText>Set Alias</MenuText>
        </MenuItem>
        <MenuItem onClick={handleClone}>
          <Icon>📋</Icon>
          <MenuText>Clone Component</MenuText>
          <Shortcut>Ctrl+C/V</Shortcut>
        </MenuItem>
        <MenuItem onClick={handleDelete} danger>
          <Icon>🗑️</Icon>
          <MenuText>Delete Component</MenuText>
          <Shortcut>Del</Shortcut>
        </MenuItem>
      </Menu>
    </>
  );
};

// Styled Components
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${THEME.zIndex.modal - 1};
`;

const Menu = styled.div`
  position: fixed;
  min-width: 200px;
  background: ${THEME.sidebarBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: ${THEME.zIndex.modal};
  padding: 4px;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s ease;
  color: ${props => props.danger ? THEME.dangerColor : THEME.textColor};

  &:hover {
    background: ${props => props.danger
      ? 'rgba(220, 20, 60, 0.15)'
      : THEME.hoverBackground};
  }
`;

const Icon = styled.span`
  font-size: 16px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
`;

const Shortcut = styled.span`
  font-size: 12px;
  color: ${THEME.textSecondary};
  opacity: 0.6;
`;

export default NodeContextMenu;
